/** @jsxImportSource react */

import { usePrefetch } from '@react/services/api';
import { pageNames, useEvents } from '@react/services/events';
import BoxedSection from '@react/shared/components/BoxedSection';
import CalculatorAndQuizzes from '@react/tools/components/CalculatorAndQuizzes';
import ToolCardList from '@react/tools/components/ToolCardList';
import {
  getIsChecklist,
  ValidCalcTools,
  ValidChecklists,
} from '@react/tools/constants/data';
import {
  transformArticles,
  transformSeniorLivingTools,
} from '@sly/frontend/common/helpers/tools';
import Accordian from '@sly/frontend/react/shared/components/Accordian';
import { ToolLayout } from '@sly/frontend/react/tools/components/Styles';
import TouringChecklistSelector from '@sly/frontend/react/tools/components/TouringChecklistSelector';
import Helmet from 'next/head';
import { useRouter } from 'next/router';
import { StrictMode, useEffect } from 'react';

export default function ToolsPage() {
  const { events } = useEvents();
  const { query } = useRouter();

  const toolName = query.toolName as ValidCalcTools | ValidChecklists;

  const {
    requestInfo: { hasFinished: toolsHasFinished, result },
  } = usePrefetch('getSeniorLivingTools', {
    'filters[slug][$eq]': toolName,
    'populate[articles][populate]': '*',
    'populate[faqs][populate]': '*',
    'populate[mainImage][populate]': '*',
  });

  const {
    requestInfo: { hasFinished: toolsPageHasFinished, result: toolsPageResult },
  } = usePrefetch('getToolsPage', {
    'populate[seniorLivingTools][populate]': '*',
    'populate[checklists][populate]': 'mainImage',
    'populate[seniorLivingTypes][populate]': 'mainImg',
  });

  const hasFinished = toolsHasFinished && toolsPageHasFinished;

  useEffect(() => {
    events.page(pageNames.Tools, {});
  }, []);

  const toolData = result?.data?.[0];

  if ((hasFinished && !toolData) || !hasFinished) return null;

  const toolsPageData = toolsPageResult?.data;

  const isChecklist = getIsChecklist(toolName);

  const faqs = toolData?.faqs || [];

  const articles = transformArticles(toolData.articles || []);

  const seniorLivingTools = transformSeniorLivingTools(
    toolsPageData?.seniorLivingTools?.filter(
      (tool: any) => tool.slug !== toolName
    ) || []
  );

  return (
    <StrictMode>
      <Helmet>
        <title>{toolData?.name} - Seniorly</title>
        {(toolData?.shortDescription || toolData?.description) && (
          <meta
            name='description'
            content={toolData?.shortDescription || toolData?.description}
          />
        )}
      </Helmet>
      <ToolLayout
        breadCrumbItems={[
          {
            label: 'Home',
            path: '/',
          },
          {
            label: 'Senior living tools',
            path: '/tools',
          },

          {
            label: toolName.split('-').join(' '),
            path: `/tools/${toolName}`,
          },
        ]}
      >
        <BoxedSection id={toolName} className='container'>
          {isChecklist ? (
            <TouringChecklistSelector
              checkListName={toolData?.toolID}
              heading={toolData?.name}
              description={toolData?.description}
              shortDescription={toolData?.shortDescription}
            />
          ) : (
            <CalculatorAndQuizzes
              toolName={toolData?.toolID}
              toolProp={toolData?.toolProp}
              heading={toolData?.name}
              description={toolData?.description}
              shortDescription={toolData?.shortDescription}
            />
          )}
        </BoxedSection>

        {!!faqs.length && (
          <BoxedSection heading='FAQ' id='faq-section' className='container'>
            <div className='grid gap-6 md:grid-cols-2'>
              {faqs.map((faq: any) => (
                <Accordian
                  openByDefault={false}
                  variant='B'
                  key={faq.title}
                  title={faq.title}
                  eventProps={{}}
                  wrapperClassName='pb-4 border-b border-b-slate-lighter-95 '
                >
                  <div
                    className='py-4'
                    dangerouslySetInnerHTML={{
                      __html: faq.description,
                    }}
                  />
                </Accordian>
              ))}
            </div>
          </BoxedSection>
        )}

        {!!seniorLivingTools?.length && (
          <BoxedSection
            heading='Other tools you might like'
            id='calculator-and-quizzes'
            className='container'
          >
            <ToolCardList items={seniorLivingTools} />
          </BoxedSection>
        )}

        <BoxedSection
          heading='Resource articles you might like'
          id='resource-articles'
          className='container'
        >
          <ToolCardList items={articles} />
        </BoxedSection>
      </ToolLayout>
    </StrictMode>
  );
}
